import React, {useMemo} from "react";
import {Collapse, ConfigProvider, Tag} from "antd";
import {RightOutlined} from "@ant-design/icons";
import {OrderActions} from "../../../event/OrderActionList/OrderActions.jsx";
import {OrderStatusView} from "../../../event/OrderStatusView/OrderStatusView.jsx";
import "./item-collapse.css";
import classnames from "classnames";
import {NOOP} from "../../../utils/NOOP.jsx";

const {Panel} = Collapse;

export const ItemCollapse = ({
    item,
    itemIndex = 0,
    onClick = NOOP,
    headers = {tags: [], description: [], onClick: NOOP, backgroundColor: () => "white"},
    body = {actions: [], style: {}}
}) => {
    const actions = useMemo(
        () =>
            body?.actions.map(action => ({
                ...action,
                ...(action.color
                    ? {color: typeof action?.color === "function" ? action?.color(item) : action.color}
                    : {}),
                loading: typeof action.loading === "function" ? action?.loading(item) : action.loading,
                ...(action.text ? {text: typeof action?.text === "function" ? action?.text(item) : action.text} : {}),
                ...(action?.onClick ? {onClick: params => action?.onClick(item, params)} : {})
            })),
        [item, body?.actions]
    );

    const itemBackgroundColor = useMemo(() => {
        if (headers?.backgroundColor) {
            return typeof headers.backgroundColor === "function"
                ? headers.backgroundColor(item)
                : headers.backgroundColor;
        }
        return "white";
    }, [headers, item]);

    return (
        <ConfigProvider direction="rtl">
            <Collapse
                className="item-collapse"
                expandIconPosition="end"
                style={{
                    backgroundColor: itemBackgroundColor,
                    "--item-bg-color": itemBackgroundColor,
                    ...body?.style
                }}
                expandIcon={({isActive}) => <RightOutlined style={{fontSize: 16}} rotate={isActive ? 90 : 0} />}
                {...(onClick !== NOOP ? {activeKey: null} : {})}>
                <Panel
                    key="panelHeader"
                    className="item-panel-container"
                    style={{backgroundColor: itemBackgroundColor, ...body?.style}}
                    header={
                        <OrderStatusView
                            onClick={onClick !== NOOP ? () => onClick(item) : NOOP}
                            className="item-collapse-container"
                            backgroundColor={itemBackgroundColor}
                            description={[
                                <>
                                    {headers?.description.map(({title, key, render, className}, index) => {
                                        return (
                                            <ItemCollapseInfoRow
                                                key={index}
                                                title={title}
                                                value={render ? render({...item, index: itemIndex}) : item?.[key]}
                                                className={className}
                                            />
                                        );
                                    })}

                                    {!!headers?.tags?.length && (
                                        <span className="item-collapse-description-tags-container">
                                            {headers?.tags
                                                ?.filter(({shouldHide}) =>
                                                    typeof shouldHide === "function" ? !shouldHide(item) : !shouldHide
                                                )
                                                ?.map(({key, render, color, className, backgroundColor}, index) => {
                                                    const bgColor = backgroundColor
                                                        ? typeof backgroundColor === "function"
                                                            ? backgroundColor(item)
                                                            : backgroundColor
                                                        : undefined;
                                                    const tagColor = color
                                                        ? typeof color === "function"
                                                            ? color(item)
                                                            : color
                                                        : undefined;
                                                    const tagClassName = className
                                                        ? typeof className === "function"
                                                            ? className(item)
                                                            : className
                                                        : undefined;
                                                    return (
                                                        <Tag
                                                            key={index}
                                                            style={{
                                                                ...(bgColor ? {backgroundColor: bgColor} : {}),
                                                                ...(tagColor ? {color: tagColor} : {})
                                                            }}
                                                            className={classnames("item-collapse-tag", tagClassName)}>
                                                            {render ? render(item) : item?.[key]}
                                                        </Tag>
                                                    );
                                                })}
                                        </span>
                                    )}
                                </>
                            ]}
                        />
                    }>
                    {body?.actions?.length ? (
                        <div className="item-collapse-content-container">
                            <OrderActions className="item-collapse-cta-container" actions={actions ?? []} />
                        </div>
                    ) : null}
                </Panel>
            </Collapse>
        </ConfigProvider>
    );
};

export const ItemCollapseInfoRow = ({className, title, value}) => {
    return (
        <>
            {!!value && (
                <span className={classnames("item-info-row", className)}>
                    {!!title && <span className="item-info-row-title">{title}</span>}
                    <span>{value}</span>
                </span>
            )}
        </>
    );
};
